import { assertSafeWorkerJson, assertWorkerTiming, isSupportedQualityVersion } from "./qualityContract";

const DB_NAME = "viewr-loader-validation";
const DB_VERSION = 2;
const LEGACY_STORE_NAME = "loaderValidationOutbox";
const STORE_NAME = "loaderValidationOutboxV2";
const MAX_PAYLOAD_BYTES = 256 * 1024;
export const LOADER_VALIDATION_OUTBOX_RETENTION_MS =
  7 * 24 * 60 * 60 * 1000;

export const LOADER_VALIDATION_EVENT_CODES = new Set([
  "LOADER_VSE_READ_SUCCESS",
  "LOADER_VSE_READ_FAILED",
  "LOADER_GLB_LOAD_SUCCESS",
  "LOADER_GLB_LOAD_FAILED",
  "LOADER_SCENE_CREATE_SUCCESS",
  "LOADER_SCENE_CREATE_FAILED",
  "LOADER_MATERIAL_METADATA_MISSING",
  "LOADER_MATERIAL_MAPPING_FAILED",
  "LOADER_MATERIAL_VALIDATION_FAILED",
  "LOADER_MATERIAL_SLOT_MISMATCH",
  "LOADER_PBR_MAPPING_MISMATCH",
  "LOADER_TEXTURE_MAPPING_MISMATCH",
  "LOADER_UV_MAPPING_MISMATCH",
  "LOADER_TRANSPARENCY_MISMATCH",
  "LOADER_DOUBLE_SIDED_MISMATCH",
  "LOADER_UNHANDLED_EXCEPTION",
]);

export interface LoaderWorkerEvent {
  [key: string]: unknown;
  stage: "loader";
  code: string;
  data: Record<string, unknown>;
  timestamp: string;
}

export type LoaderFragmentContractVersion = string;

export interface LoaderWorkerFragment {
  [key: string]: unknown;
  contractVersion: LoaderFragmentContractVersion;
  jobId: string;
  stage: "loader";
  outcome: "success" | "failed";
  events: LoaderWorkerEvent[];
}

export interface LoaderValidationEventPayload {
  source_conversion_job_id: string;
  loaded_vse_gcs_path: string;
  loader_job_id: string;
  generation: number;
  outcome: "success" | "failed";
  worker_fragment: LoaderWorkerFragment;
}

export type LoaderValidationOutboxState =
  | "pending"
  | "submitting"
  | "accepted"
  | "rejected";

export interface LoaderValidationOutboxRow {
  attemptKey: string;
  loaderJobId: string;
  generation: number;
  outcome: "success" | "failed";
  ownerUid: string;
  sourceConversionJobId: string;
  loadedVseGcsPath: string;
  workerFragment: LoaderWorkerFragment;
  requestFingerprint: string;
  state: LoaderValidationOutboxState;
  attemptCount: number;
  nextAttemptAt: number;
  createdAt: number;
  updatedAt: number;
  lastErrorCode?: string;
}

export function isLoaderValidationRowDue(
  row: LoaderValidationOutboxRow,
  now: number,
): boolean {
  return row.state === "pending" && row.nextAttemptAt <= now;
}

export function recoverInterruptedLoaderValidationRow(
  row: LoaderValidationOutboxRow,
  now: number,
): LoaderValidationOutboxRow {
  if (row.state !== "submitting") return row;
  return {
    ...row,
    state: "pending",
    nextAttemptAt: now,
    updatedAt: now,
  };
}

export function shouldPruneLoaderValidationRow(
  row: LoaderValidationOutboxRow,
  now: number,
): boolean {
  return row.createdAt < now - LOADER_VALIDATION_OUTBOX_RETENTION_MS;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function assertExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
  label: string,
): void {
  const expectedSet = new Set(expected);
  const unknown = Object.keys(value).filter((key) => !expectedSet.has(key));
  if (unknown.length > 0) {
    throw new Error(`${label}_unknown_fields:${unknown.join(",")}`);
  }
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJson).join(",")}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function encodeUtf8(value: string): Uint8Array {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(value);
  }
  const binary = unescape(encodeURIComponent(value));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

async function fingerprintPayload(payload: LoaderValidationEventPayload): Promise<string> {
  const bytes = encodeUtf8(stableJson(payload));
  if (globalThis.crypto?.subtle) {
    const digestInput = Uint8Array.from(bytes).buffer as ArrayBuffer;
    const digest = await globalThis.crypto.subtle.digest("SHA-256", digestInput);
    return Array.from(new Uint8Array(digest))
      .map((value) => value.toString(16).padStart(2, "0"))
      .join("");
  }

  let hash = 2166136261;
  for (let index = 0; index < bytes.length; index += 1) {
    const byte = bytes[index];
    hash ^= byte;
    hash = Math.imul(hash, 16777619);
  }
  return `fallback-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function parseLoaderValidationEventPayload(
  input: unknown,
): LoaderValidationEventPayload {
  const parsed =
    typeof input === "string"
      ? (() => {
          try {
            return JSON.parse(input) as unknown;
          } catch {
            throw new Error("loader_validation_payload_invalid_json");
          }
        })()
      : input;

  if (!isRecord(parsed)) {
    throw new Error("loader_validation_payload_not_object");
  }
  assertExactKeys(
    parsed,
    [
      "source_conversion_job_id",
      "loaded_vse_gcs_path",
      "loader_job_id",
      "generation",
      "outcome",
      "worker_fragment",
    ],
    "loader_validation_payload",
  );

  if (typeof parsed.source_conversion_job_id !== "string" || typeof parsed.loader_job_id !== "string") {
    throw new Error("loader_validation_correlation_id_missing");
  }
  const sourceConversionJobId = parsed.source_conversion_job_id.trim();
  const loadedVseGcsPath = String(parsed.loaded_vse_gcs_path ?? "").trim();
  const loaderJobId = parsed.loader_job_id.trim();
  const generation = parsed.generation;
  const outcome = parsed.outcome;
  if (!sourceConversionJobId || !loaderJobId || sourceConversionJobId.length > 128 || loaderJobId.length > 128) {
    throw new Error("loader_validation_correlation_id_missing");
  }
  if (
    typeof generation !== "number" ||
    !Number.isSafeInteger(generation) ||
    generation <= 0 ||
    (outcome !== "success" && outcome !== "failed")
  ) {
    throw new Error("loader_validation_attempt_invalid");
  }
  if (
    !/^(?:gs|s3):\/\/[^/?#]+\/[^?#]+\.vse$/i.test(loadedVseGcsPath) ||
    loadedVseGcsPath.includes("..")
  ) {
    throw new Error("loader_validation_vse_path_invalid");
  }

  if (!isRecord(parsed.worker_fragment)) {
    throw new Error("loader_validation_fragment_not_object");
  }
  const fragment = parsed.worker_fragment;
  assertSafeWorkerJson(fragment);
  assertWorkerTiming(fragment);
  const fragmentContractVersion = fragment.contractVersion;
  if (
    !isSupportedQualityVersion(fragmentContractVersion) ||
    fragment.jobId !== loaderJobId ||
    fragment.stage !== "loader" ||
    fragment.outcome !== outcome ||
    !Array.isArray(fragment.events) ||
    fragment.events.length === 0
  ) {
    throw new Error("loader_validation_fragment_contract_invalid");
  }

  const events = fragment.events.map((event, index): LoaderWorkerEvent => {
    if (!isRecord(event)) {
      throw new Error(`loader_validation_event_invalid:${index}`);
    }
    if ("severity" in event || "message" in event) {
      throw new Error(`loader_validation_event:${index}_unknown_fields`);
    }
    if (
      event.stage !== "loader" ||
      typeof event.code !== "string" ||
      !LOADER_VALIDATION_EVENT_CODES.has(event.code) ||
      !isRecord(event.data) ||
      typeof event.timestamp !== "string" ||
      !event.timestamp.trim()
    ) {
      throw new Error(`loader_validation_event_contract_invalid:${index}`);
    }
    return event as unknown as LoaderWorkerEvent;
  });
  const errorCodes = new Set([
    "LOADER_VSE_READ_FAILED", "LOADER_GLB_LOAD_FAILED",
    "LOADER_SCENE_CREATE_FAILED", "LOADER_UNHANDLED_EXCEPTION",
  ]);
  if (events.some((event) => errorCodes.has(event.code)) !== (outcome === "failed")) {
    throw new Error("loader_validation_fragment_outcome_invalid");
  }

  const payload: LoaderValidationEventPayload = {
    source_conversion_job_id: sourceConversionJobId,
    loaded_vse_gcs_path: loadedVseGcsPath,
    loader_job_id: loaderJobId,
    generation,
    outcome,
    // Clone all validated JSON fields so pending runtime objects cannot mutate
    // the durable intent. Do not rebuild the fragment from a UI projection.
    worker_fragment: JSON.parse(JSON.stringify(fragment)) as LoaderWorkerFragment,
  };
  if (encodeUtf8(JSON.stringify(payload)).byteLength > MAX_PAYLOAD_BYTES) {
    throw new Error("loader_validation_payload_too_large");
  }
  return payload;
}

function openOutboxDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("loader_validation_indexeddb_unavailable"));
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "attemptKey" });
        store.createIndex("state_nextAttemptAt", ["state", "nextAttemptAt"], {
          unique: false,
        });

        if (db.objectStoreNames.contains(LEGACY_STORE_NAME)) {
          const legacyStore = request.transaction?.objectStore(LEGACY_STORE_NAME);
          const cursorRequest = legacyStore?.openCursor();
          if (cursorRequest) {
            cursorRequest.onsuccess = () => {
              const cursor = cursorRequest.result;
              if (!cursor) return;
              const legacy = cursor.value as Partial<LoaderValidationOutboxRow>;
              const loaderJobId = String(legacy.loaderJobId ?? "").trim();
              if (loaderJobId) {
                store.put({
                  ...legacy,
                  attemptKey: `${loaderJobId}:1`,
                  generation: 1,
                  outcome:
                    legacy.workerFragment?.outcome === "failed"
                      ? "failed"
                      : "success",
                });
              }
              cursor.continue();
            };
          }
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("loader_validation_indexeddb_open_failed"));
  });
}

async function runOutboxTransaction<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore, resolveValue: (value: T) => void) => void,
): Promise<T> {
  const db = await openOutboxDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let result!: T;
    run(store, (value) => {
      result = value;
    });
    transaction.oncomplete = () => {
      db.close();
      resolve(result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error ?? new Error("loader_validation_indexeddb_transaction_failed"));
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error ?? new Error("loader_validation_indexeddb_transaction_aborted"));
    };
  });
}

export async function persistLoaderValidationEvent(
  input: unknown,
  ownerUid: string,
): Promise<LoaderValidationOutboxRow> {
  const normalizedOwnerUid = ownerUid.trim();
  if (!normalizedOwnerUid) {
    throw new Error("loader_validation_owner_unavailable");
  }
  const payload = parseLoaderValidationEventPayload(input);
  const requestFingerprint = await fingerprintPayload(payload);
  const now = Date.now();
  const attemptKey = `${payload.loader_job_id}:${payload.generation}`;

  return runOutboxTransaction<LoaderValidationOutboxRow>("readwrite", (store, setResult) => {
    const getRequest = store.get(attemptKey);
    getRequest.onsuccess = () => {
      const existing = getRequest.result as LoaderValidationOutboxRow | undefined;
      if (existing) {
        if (
          existing.requestFingerprint !== requestFingerprint ||
          existing.ownerUid !== normalizedOwnerUid
        ) {
          store.transaction.abort();
          return;
        }
        setResult(existing);
        return;
      }

      const row: LoaderValidationOutboxRow = {
        attemptKey,
        loaderJobId: payload.loader_job_id,
        generation: payload.generation,
        outcome: payload.outcome,
        ownerUid: normalizedOwnerUid,
        sourceConversionJobId: payload.source_conversion_job_id,
        loadedVseGcsPath: payload.loaded_vse_gcs_path,
        workerFragment: payload.worker_fragment,
        requestFingerprint,
        state: "pending",
        attemptCount: 0,
        nextAttemptAt: now,
        createdAt: now,
        updatedAt: now,
      };
      store.put(row);
      setResult(row);
    };
  }).catch((error) => {
    if (error instanceof Error && error.message.includes("transaction_aborted")) {
      throw new Error("loader_validation_loader_job_conflict");
    }
    throw error;
  });
}

export async function listDueLoaderValidationRows(
  now = Date.now(),
): Promise<LoaderValidationOutboxRow[]> {
  return runOutboxTransaction<LoaderValidationOutboxRow[]>("readonly", (store, setResult) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const rows = (request.result as LoaderValidationOutboxRow[])
        .filter((row) => isLoaderValidationRowDue(row, now))
        .sort((a, b) => a.createdAt - b.createdAt);
      setResult(rows);
    };
  });
}

export async function updateLoaderValidationRow(
  attemptKey: string,
  update: (row: LoaderValidationOutboxRow) => LoaderValidationOutboxRow,
): Promise<LoaderValidationOutboxRow | null> {
  return runOutboxTransaction<LoaderValidationOutboxRow | null>("readwrite", (store, setResult) => {
    const request = store.get(attemptKey);
    request.onsuccess = () => {
      const row = request.result as LoaderValidationOutboxRow | undefined;
      if (!row) {
        setResult(null);
        return;
      }
      const next = update(row);
      store.put(next);
      setResult(next);
    };
  });
}

export async function recoverInterruptedLoaderValidationRows(): Promise<void> {
  const now = Date.now();
  const rows = await runOutboxTransaction<LoaderValidationOutboxRow[]>("readonly", (store, setResult) => {
    const request = store.getAll();
    request.onsuccess = () => setResult(request.result as LoaderValidationOutboxRow[]);
  });
  await Promise.all(
    rows
      .filter((row) => row.state === "submitting")
      .map((row) =>
        updateLoaderValidationRow(row.attemptKey, (current) =>
          recoverInterruptedLoaderValidationRow(current, now),
        ),
      ),
  );
}

export async function pruneExpiredLoaderValidationRows(now = Date.now()): Promise<void> {
  await runOutboxTransaction<void>("readwrite", (store, setResult) => {
    const request = store.getAll();
    request.onsuccess = () => {
      for (const row of request.result as LoaderValidationOutboxRow[]) {
        if (shouldPruneLoaderValidationRow(row, now)) {
          store.delete(row.attemptKey);
        }
      }
      setResult(undefined);
    };
  });
}
